import { Fragment, useEffect } from "react"

import { Flex } from "@/atoms"

import { useLogger } from "@/hooks/useLogger"
import { ReadInstructionButton } from "@/molecules/ReadInstructionButton"
import { AboutSection } from "@/pages/IntegrationsPage/components/molecules/AboutSection"
import { InstallStepsSection } from "@/pages/IntegrationsPage/components/molecules/InstallStepsSection"
import { PrerequisitesSection } from "@/pages/IntegrationsPage/components/molecules/PrerequisitesSection"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"

import type { BitbucketConfigurationTabProps } from "."

export const BitbucketConnectTab = (props: BitbucketConfigurationTabProps) => {
  const { logger, EVENTS } = useLogger()
  const [data] = useIntegration(props)
  const { connectPage = {}, name } = data?.integration || {}
  const { sections = [] } = connectPage

  const onButtonClick = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.OPENED_INSTRUCTION, {
      name
    })
  }

  useEffect(() => {
    if (!sections?.length) return

    const dataAttribute = "data-anchor"
    const anchorElementTag = "i"

    const cleanups: Array<() => void> = []

    document
      .querySelectorAll<HTMLElement>(`[${dataAttribute}]`)
      .forEach((element) => {
        const anchorId = element.getAttribute(dataAttribute)

        if (!anchorId) return

        const anchorElement = document.getElementById(anchorId)

        if (!anchorElement) return

        const target = element.getElementsByTagName(anchorElementTag)[0]

        if (!target) return

        const scrollFn = () => {
          anchorElement.scrollIntoView({ behavior: "smooth" })
        }

        target.addEventListener("click", scrollFn)
        cleanups.push(() => target.removeEventListener("click", scrollFn))
      })

    return () => {
      cleanups.forEach((cleanup) => cleanup())
    }
  }, [sections])

  return (
    <>
      {sections.map((section, index) => {
        if (section.installSteps)
          return (
            <InstallStepsSection
              key={index}
              section={section}
              onButtonClick={onButtonClick}
            />
          )

        if (section.prerequisites)
          return (
            <Fragment key={index}>
              <PrerequisitesSection section={section} />
              {section.instructionsLink && (
                <Flex justify="flex-end">
                  <ReadInstructionButton href={section.instructionsLink} />
                </Flex>
              )}
            </Fragment>
          )

        return (
          <AboutSection
            key={index}
            section={section}
            hasDivider={index === 0}
          />
        )
      })}
    </>
  )
}
